/* eslint-disable @typescript-eslint/no-empty-interface */
import { ThemeOptions, Theme } from '@mui/material/styles'
// import Box from '@mui/material/Box'
// import { autocompleteClasses } from '@mui/material/Autocomplete'

// import type { Options } from './utils/options'

declare module '@mui/material/styles' {
  interface CustomThemeOptions {
    boxShadows: {
      main: string
      modal: string
    }

    colors: {
      whiteBackground: string
      secondaryBackground: string
      tertiaryBackground: string
      greyBackground: string
      accent: {
        200: string
        500: string
        800: string
      }
      primary: string
      secondary: string
      disabled: string
      transparent: string
    }
  }
  interface Theme extends CustomThemeOptions {}
  interface ThemeOptions extends Partial<CustomThemeOptions> {}
}

const colors = {
  // Light theme
  light: {
    whiteBackground: '#FFFFFF',
    secondaryBackground: '#F6F9FC',
    tertiaryBackground: '#E8F0FA',
    greyBackground: '#F5F5F5',
    accent: {
      200: '#89C7D2',
      500: '#3B84A2',
      800: '#10557A',
    },
    primary: '#B3D3DC',
    secondary: '#D9D9D9',
    disabled: '#9C9C9C',
    transparent: 'transparent',
  },
  // Dark theme
  dark: {
    whiteBackground: '#121212',
    secondaryBackground: '#1E1E1E',
    tertiaryBackground: '#2C2C2C',
    greyBackground: '#3A3A3A',
    accent: {
      200: '#89C7D2',
      500: '#3B84A2',
      800: '#10557A',
    },
    primary: '#90CAF9',
    secondary: '#F48FB1',
    disabled: '#616161',
    transparent: 'transparent',
  },
} as const

export const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    common: {
      black: '#000',
      white: '#fff',
    },
    primary: {
      main: colors.light.accent[500],
      contrastText: '#F5F5F5',
    },
    secondary: {
      main: '#D93F46',
      contrastText: '#F5F5F5',
    },
    background: {
      paper: '#ffffff',
      default: '#F5F5F5',
    },
    divider: '#EEF0F2',
    info: {
      main: '#006AB3',
    },
    success: {
      main: '#009688',
    },
    error: {
      main: '#B10818',
    },
    warning: {
      main: '#f69342',
    },
    text: {
      primary: '#333333',
      secondary: '#707070',
    },
  },
  typography: {
    htmlFontSize: 14,
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 700,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 700,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 700,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
    },
  },
  boxShadows: {
    main: '0px 0.25rem 0.25rem rgba(0, 0, 0, 0.25)',
    modal: '0px 0.25rem 0.25rem rgba(0, 0, 0, 0.25)',
  },
  colors: colors.light,
  components: {
    MuiInputBase: {
      defaultProps: {
        disableInjectingGlobalStyles: true,
      },
      styleOverrides: {
        input: {
          '&.Mui-disabled': {
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.8)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderWidth: '0.15rem',
          borderColor: '#D9D9D9',
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.light.greyBackground,
          borderRadius: '0.6rem',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          lineHeight: 1,
          height: '2.9rem',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          lineHeight: 1,
          borderRadius: '0.6rem',
        },
        outlined: {
          border: `0.15rem solid ${colors.light.secondary}`,
          backgroundColor: colors.light.secondaryBackground,
          color: colors.light.accent[200],
          ':hover': {
            borderWidth: '0.15rem',
            color: colors.light.accent[200],
            backgroundColor: colors.light.whiteBackground,
            borderColor: colors.light.accent[200],
          },
        },
        contained: {
          border: `0.15rem solid ${colors.light.accent[800]}`,
          backgroundColor: colors.light.accent[500],
          color: '#FFFFFF',
          ':hover': {
            borderWidth: '0.15rem',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          height: '38px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderBottomWidth: '0.15rem',
          borderRightWidth: '0.15rem',
          marginTop: '0.8rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    // MuiAutocomplete: {
    //   defaultProps: {
    //     getOptionLabel(option) {
    //       return (option as Options).value
    //     },
    //     renderOption: (props, option) => (
    //       <Box
    //         sx={{
    //           borderRadius: '8px',
    //           margin: '5px',
    //           [`&.${autocompleteClasses.option}`]: {
    //             padding: '8px',
    //           },
    //         }}
    //         component="li"
    //         {...props}
    //       >
    //         {(option as Options).label}
    //       </Box>
    //     ),
    //   },
    // },
  },
}

export const globalStyles = (theme: Theme) => ({
  html: {
    margin: 0,
    padding: 0,
    minHeight: '100%',
    overflow: 'hidden',
    fontSize: '87.5%',
    [theme.breakpoints.down('md')]: {
      fontSize: '81.25%',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '75%',
    },
  },
  '*': {
    boxSizing: 'border-box',
  },
  body: {
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    margin: 0,
    padding: 0,
  },
  h1: {
    fontSize: theme.typography.h1.fontSize,
    fontWeight: theme.typography.h1.fontWeight,
    marginBlock: '0.5em',
  },
  h2: {
    fontSize: theme.typography.h2.fontSize,
    fontWeight: theme.typography.h2.fontWeight,
    marginBlock: '0.5em',
  },
  h3: {
    fontSize: theme.typography.h3.fontSize,
    fontWeight: theme.typography.h3.fontWeight,
    marginBlock: '0.5em',
  },
  h4: {
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.h4.fontWeight,
    marginBlock: '0.5em',
  },
  h5: {
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.h5.fontWeight,
    marginBlock: '0.5em',
  },
  h6: {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.h6.fontWeight,
    marginBlock: '0.5em',
  },
  '@keyframes mui-auto-fill': { from: { display: 'block' } },
  '@keyframes mui-auto-fill-cancel': { from: { display: 'block' } },
})
